/* Gestione persistenza: acquisizione soft-lock al primo edit, autosave
   debounced, heartbeat per tenere il lock, gestione conflitti (409). */
import type { DiagramDoc } from "@fullsteps/shared";
import { api, ApiError, type LockInfo, type User } from "../api.js";

export type SaveStatus = "idle" | "dirty" | "saving" | "saved" | "error" | "conflict" | "locked";

export class SaveManager {
  private baseVersionId: number | null;
  private lock: LockInfo | null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private pending: DiagramDoc | null = null;
  private hasLock = false;

  onStatus: (s: SaveStatus, detail?: string) => void = () => {};
  onConflict: () => void = () => {};
  onSaved: (versionId: number) => void = () => {};

  constructor(
    private diagramId: number,
    private user: User,
    baseVersionId: number | null,
    lock: LockInfo | null,
  ) {
    this.baseVersionId = baseVersionId;
    this.lock = lock;
  }

  get versionId(): number | null {
    return this.baseVersionId;
  }

  /** Lock attivo tenuto da un altro utente (per mostrare avviso "solo lettura"). */
  lockedByOther(): LockInfo | null {
    if (
      this.lock &&
      this.lock.userId !== this.user.id &&
      new Date(this.lock.expiresAt).getTime() > Date.now()
    )
      return this.lock;
    return null;
  }

  private async ensureLock(): Promise<boolean> {
    if (this.hasLock) return true;
    try {
      const r = await api.acquireLock(this.diagramId);
      this.hasLock = true;
      this.lock = r.lock;
      if (!this.heartbeat)
        this.heartbeat = setInterval(() => void api.acquireLock(this.diagramId).catch(() => {}), 30_000);
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const name = (e.body as { lock?: { name?: string } })?.lock?.name;
        this.onStatus("locked", name ?? undefined);
        return false;
      }
      throw e;
    }
  }

  /** Segna il documento come sporco e programma il salvataggio. */
  schedule(doc: DiagramDoc): void {
    this.pending = doc;
    this.onStatus("dirty");
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), 900);
  }

  async flush(): Promise<void> {
    if (!this.pending) return;
    const doc = this.pending;
    this.pending = null;
    if (!(await this.ensureLock())) return;
    this.onStatus("saving");
    try {
      const r = await api.save(this.diagramId, doc, undefined, this.baseVersionId);
      this.baseVersionId = r.version.id;
      this.onSaved(r.version.id);
      this.onStatus("saved");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        this.onStatus("conflict");
        this.onConflict();
      } else {
        this.onStatus("error", e instanceof Error ? e.message : String(e));
      }
    }
  }

  setBaseVersion(id: number): void {
    this.baseVersionId = id;
  }

  async release(): Promise<void> {
    if (this.heartbeat) clearInterval(this.heartbeat);
    if (this.hasLock) await api.releaseLock(this.diagramId).catch(() => {});
  }
}
